import type { StoryboardBoardTrackWithItems } from '@/types';
import type { StoryboardItem } from '@/types/storyboard';

export function replaceStoryboardBoardResourceItem(
  tracks: StoryboardBoardTrackWithItems[],
  updatedItem: StoryboardItem
): StoryboardBoardTrackWithItems[] {
  return tracks.map((track) => {
    if (track.type !== 'resource') return track;

    return {
      ...track,
      items: track.items.map((item) => {
        if (item.id !== updatedItem.itemId) return item;

        const nextTitle = updatedItem.elementName || updatedItem.tagLabel || item.title || '';

        return {
          ...item,
          resourceId: updatedItem.resourceId,
          tagId: updatedItem.elementTagId,
          tagLabel: updatedItem.tagLabel,
          elementName: updatedItem.elementName,
          title: nextTitle,
          coverUrl: updatedItem.coverUrl ?? item.coverUrl,
        };
      }),
    };
  });
}

/**
 * 就地更新分镜板资源轨道项的名称（footage 重命名无感同步）。
 * 按 resourceId 匹配资源轨道项，同步 resourceName 与 UI 展示用 title，不触发重拉。
 */
export function renameStoryboardBoardResourceItems(
  tracks: StoryboardBoardTrackWithItems[],
  target: { resourceId: string; name: string }
): StoryboardBoardTrackWithItems[] {
  const normalizedResourceId = String(target.resourceId ?? '').trim();
  if (!normalizedResourceId) return tracks;

  return tracks.map((track) => {
    if (track.type !== 'resource') return track;

    return {
      ...track,
      items: track.items.map((item) => {
        const itemResourceId = String(item.resourceId ?? '').trim();
        const itemEntityId = String(item.entityId ?? '').trim();
        const matches =
          itemResourceId === normalizedResourceId || itemEntityId === normalizedResourceId;
        if (!matches) return item;

        // footage chip 展示名优先取 elementName，其次 resourceName，故三者同步更新
        return {
          ...item,
          resourceName: target.name,
          elementName: target.name,
          title: target.name,
        };
      }),
    };
  });
}

export function removeStoryboardBoardResourceItems(
  tracks: StoryboardBoardTrackWithItems[],
  target: { resourceId?: string | null; elementId?: string | null }
): StoryboardBoardTrackWithItems[] {
  const normalizedResourceId = String(target.resourceId ?? '').trim();
  const normalizedElementId = String(target.elementId ?? '').trim();

  return tracks.map((track) => {
    if (track.type !== 'resource') return track;

    return {
      ...track,
      items: track.items.filter((item) => {
        const itemResourceId = String(item.resourceId ?? '').trim();
        const itemElementId = String(item.elementId ?? '').trim();
        const itemEntityId = String(item.entityId ?? '').trim();

        if (normalizedResourceId) {
          const matchesResource =
            itemResourceId === normalizedResourceId || itemEntityId === normalizedResourceId;
          if (matchesResource) return false;
        }

        if (normalizedElementId) {
          const matchesElement =
            itemElementId === normalizedElementId || itemEntityId === normalizedElementId;
          if (matchesElement) return false;
        }

        return true;
      }),
    };
  });
}
